import GetFieldsResponse from "@/schemas/GetFieldsResponse";
import UsimStatusResponse, {
  UserActivationItem,
  UsimOrderItem,
  UsimOrdersResponse,
} from "@/schemas/UsimOrdersResponse";
import GetRedemptionDataRequest from "@/schemas/GetRedemptionDataRequest";
import GetRedemptionDataResponse from "@/schemas/GetRedemptionDataResponse";
import RedeemRequest from "@/schemas/RedeemRequest";
import EsimUsageResponse from "@/schemas/EsimUsageResponse";
import EsimStatusResponse from "@/schemas/EsimStatusResponse";
import env from "../utils/env";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";
import GetTgtUsageRequest from "@/schemas/GetTgtUsageRequest";
import GetTgtUsageResponse from "@/schemas/GetTgtUsageResponse";
import GetTgtOrderResponse from "@/schemas/GetTgtOrderResponse";

// const BASE_URL = env("REACT_APP_REDEMPTION_API_URL");
const BASE_URL = env("REACT_APP_REDEMPTION_API_URL");
const ESIM_USAGE_URL = env("REACT_APP_ESIM_USAGE_URL");
const APP_ID = env("REACT_APP_ESIM_USAGE_APP_ID");
const APP_SECRET = env("REACT_APP_ESIM_USAGE_APP_SECRET");
const USIM_ORDERS_API = env("REACT_APP_USIM_ORDERS_API");


const USIM_BASE_URL = "https://esim.joytelecom.com/openapi";
const PROXY_URL = 'https://acrp.shukran.tech/usim/rechargeOrder/query'
// Create a base query for the redemption API
const redemptionBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  mode: "cors",
});

type OrderSimIndicesResponse = {
  simIdx: number;
  title?: string;
};

export type GetFormFieldsResponse = {
  title?: string;
  description?: string;
  image?: string;
  fields: import("@/schemas/GetRedemptionDataResponse").RedemptionDataKycField[];
};

export type AusregSubmitRequest = {
  OrderNumber: string;
  shopId: string;
  kycDetails: Record<string, string>;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: redemptionBaseQuery,
  endpoints: (builder) => ({
    getRedemption: builder.query<
      GetRedemptionDataResponse,
      GetRedemptionDataRequest
    >({
      query: ({ orderNo, simIdx }) => ({
        url: `redeem?order_id=${orderNo}&sim_idx=${simIdx}`,
        method: "GET",
      }),
    }),
    getOrderSimIndices: builder.query<OrderSimIndicesResponse[], { orderNo: string; maxSimIdx?: number }>({
      queryFn: async ({ orderNo, maxSimIdx = 50 }, _queryApi, _extraOptions, baseQuery) => {
        const simIndices: OrderSimIndicesResponse[] = [];

        for (let simIdx = 1; simIdx <= maxSimIdx; simIdx++) {
          const result = await baseQuery({
            url: `redeem?order_id=${orderNo}&sim_idx=${simIdx}`,
            method: "GET",
          });

          if (result.error) {
            if (simIdx === 1) {
              return { error: result.error };
            }
            break;
          }

          const redemptionData = result.data as GetRedemptionDataResponse;
          simIndices.push({
            simIdx,
            title: redemptionData.title,
          });
        }

        return { data: simIndices };
      },
    }),
    postRedemption: builder.mutation<void, RedeemRequest>({
      query: ({ orderNo, simIdx, fields }) => ({
        url: `redeem?order_id=${orderNo}&sim_idx=${simIdx}`,
        method: "POST",
        body: { fields },
      }),
    }),
    getEsimUsage: builder.query<EsimUsageResponse, { coupon: string }>({
      queryFn: async ({ coupon }) => {
        // Generate authentication headers
        const timestamp = new Date().getTime();
        const transId = uuidv4();
        const plainText = APP_ID + transId + timestamp + APP_SECRET;
        const cipherText = CryptoJS.MD5(plainText).toString();

        try {
          // Use the esimUsageBaseQuery directly
          const response = await fetch(`${ESIM_USAGE_URL}/esim/usage/query`, {
            method: "POST",
            headers: {
              AppId: APP_ID,
              TransId: transId,
              Timestamp: timestamp.toString(),
              Ciphertext: cipherText,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ coupon }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
    }),

    getEsimStatus: builder.query<EsimStatusResponse, { coupon: string }>({
      queryFn: async ({ coupon }) => {
        // Generate authentication headers
        const timestamp = new Date().getTime();
        const transId = uuidv4();
        const plainText = APP_ID + transId + timestamp + APP_SECRET;
        const cipherText = CryptoJS.MD5(plainText).toString();

        try {
          // Call the eSIM status API
          const response = await fetch("/api/joytel/status", {
            method: "POST",
            headers: {
              AppId: APP_ID,
              TransId: transId,
              Timestamp: timestamp.toString(),
              Ciphertext: cipherText,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ coupon }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
    }),

    getFormFields: builder.query<GetFormFieldsResponse, { formId: string }>({
      query: ({ formId }) => ({
        url: `form?id=${encodeURIComponent(formId)}`,
        method: "GET",
      }),
      transformResponse: (response: {
        Id: string;
        RedemptionFields: GetFormFieldsResponse["fields"];
      }) => ({ fields: response.RedemptionFields ?? [] }),
    }),
    postAusregSubmission: builder.mutation<void, AusregSubmitRequest>({
      query: (body) => ({
        url: `${USIM_ORDERS_API}?action=submitAusKyc`,
        method: "POST",
        body: body,
      }),
    }),
    postGetKycOrder: builder.mutation<UsimOrderItem[], {orderNumber: string}>({
      query: ({orderNumber}) => ({
        url: `${USIM_ORDERS_API}?action=getKycOrder`,
        method: "POST",
        body: {orderNumber},
      }),
    }),
    getTgtUsage: builder.query<GetTgtUsageResponse, GetTgtUsageRequest>({
      query: ({ orderNo, simIdx }) => ({
        url: `tgt-usage?orderId=${orderNo}&simIdx=${simIdx}`,
        method: "GET",
      }),
    }),

    getTgtOrder: builder.query<GetTgtOrderResponse, GetTgtUsageRequest>({
      query: ({ orderNo, simIdx }) => ({
        url: `tgt-order?orderId=${orderNo}&simIdx=${simIdx}`,
        method: "GET",
      }),
    }),

    getUsimOrders: builder.query<UsimOrdersResponse, { orderNo: string }>({
      query: ({ orderNo }) => ({
        url: `${USIM_ORDERS_API}?orderNumber=${orderNo}`,
        method: "GET",
      }),
    }),
    updateActivationDate: builder.mutation<void, UserActivationItem[]>({
      query: (body) => ({
        url: `${USIM_ORDERS_API}?action=update-user-activation`,
        method: "PUT",
        body,
      }),
    }),
    // get usim status
    getUsimUsage: builder.query<UsimStatusResponse, { cid: string , orderId: string}>({
      queryFn: async ({ cid , orderId }) => {
        // Generate authentication headers
        const timestamp = new Date().getTime();
        const transId = uuidv4();
        const plainText = APP_ID + transId + timestamp + APP_SECRET;
        const cipherText = CryptoJS.MD5(plainText).toString();

        try {
          // Call the eSIM status API
          const response = await fetch(`${USIM_BASE_URL}/sim/usage/query`, {
            method: "POST",
            headers: {
              AppId: APP_ID,
              TransId: transId,
              Timestamp: timestamp.toString(),
              Ciphertext: cipherText,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ cid: cid, orderId :  orderId}),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
    }),
  }),
});

export const {
  useGetRedemptionQuery,
  useGetOrderSimIndicesQuery,
  usePostRedemptionMutation,
  useGetEsimUsageQuery,
  useGetEsimStatusQuery,
  useGetTgtUsageQuery,
  useGetUsimOrdersQuery,
  useGetUsimUsageQuery,
  useUpdateActivationDateMutation,
  useGetTgtOrderQuery,
  useGetFormFieldsQuery,
  usePostAusregSubmissionMutation,
  usePostGetKycOrderMutation,
} = apiSlice;
